import { useState } from 'react';

import { useSubscription } from '@apollo/client';
import styled from 'styled-components';

import { PAGES } from '../../constants/pages';

import {
    SiteSetupCreatedSubscription,
    SiteSetupState,
    SiteSetupUpdatedSubscription,
    SiteType,
    useGetSimpleMicrostoreSitesQuery,
    useGetSiteSetupsForDateQuery,
} from 'data/__generated__';

import { SITE_SETUP_CREATED_SUBSCRIPTION, SITE_SETUP_UPDATED_SUBSCRIPTION } from 'data/subscriptions/siteSetup';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { TrelloView, TrelloViewDataType } from 'components/TrelloView/TrelloView';
import { SiteSetupTrelloItem } from './components/SiteSetupTrelloItem';
import { SiteWithoutSetupTrelloItem } from './components/SiteWithoutSetupTrelloItem';

import { TotemDatePicker } from 'components/TotemDatePicker';
import { formatDateAsAnniversary } from 'helpers/dateTimes';
import { siteSetupCreatedHandler, siteSetupUpdatedHandler } from './cacheHandlers/siteSetupsCacheHandler';
import { useSearchValueParam } from 'helpers/SearchParams/utils';
import { TotemInput } from 'components/TotemInput';

export const SiteSetups = () => {
    const { searchInputValue, searchValue, setSearchInputValue } = useSearchValueParam();
    const [setupsDate, setSetupsDate] = useState<Date>(new Date());

    const {
        data: siteSetupsData,
        error: siteSetupsError,
        loading: siteSetupsLoading,
    } = useGetSiteSetupsForDateQuery({
        fetchPolicy: 'cache-and-network',
        variables: { date: formatDateAsAnniversary({ dateTime: setupsDate }) },
    });
    const {
        data: sitesData,
        loading: sitesLoading,
        error: sitesError,
    } = useGetSimpleMicrostoreSitesQuery({ fetchPolicy: 'cache-and-network' });

    useSubscription<SiteSetupCreatedSubscription>(SITE_SETUP_CREATED_SUBSCRIPTION, {
        onData({ data: { data } }) {
            if (!data) {
                return;
            }
            const { siteSetupCreated } = data;
            siteSetupCreatedHandler(siteSetupCreated);
        },
    });

    useSubscription<SiteSetupUpdatedSubscription>(SITE_SETUP_UPDATED_SUBSCRIPTION, {
        onData({ data: { data } }) {
            if (!data) {
                return;
            }
            const { siteSetupUpdated } = data;
            siteSetupUpdatedHandler(siteSetupUpdated);
        },
    });

    if ((siteSetupsLoading && !siteSetupsData) || (sitesLoading && !sitesData)) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (siteSetupsError || !siteSetupsData || sitesError || !sitesData) {
        throw new Error('Une erreur est survenue lors de la récupération des rangements');
    }

    const { getSiteSetupsForDate: siteSetups } = siteSetupsData;
    const { microstoreSites: sites } = sitesData;

    function getSiteSetupsByState({ state, isLate }: { state?: SiteSetupState; isLate?: boolean }) {
        return siteSetups
            .filter((siteSetup) =>
                isLate
                    ? siteSetup.date !== formatDateAsAnniversary({ dateTime: setupsDate })
                    : siteSetup.date === formatDateAsAnniversary({ dateTime: setupsDate }) && siteSetup.state === state,
            )
            .filter(
                (siteSetup) =>
                    !searchValue ||
                    siteSetup._id.toLowerCase().includes(searchValue.toLowerCase()) ||
                    siteSetup.site.name.toLowerCase().includes(searchValue.toLowerCase()),
            )
            .map((siteSetup) => {
                return {
                    _id: siteSetup._id,
                    content: <SiteSetupTrelloItem siteSetup={siteSetup} showDate={isLate} />,
                };
            });
    }

    const siteWithoutSetupItems = sites
        .filter((site) => site.type !== SiteType.Warehouse && !siteSetups.find(({ siteId }) => siteId === site._id))
        .sort((siteA, siteB) => siteA.name.localeCompare(siteB.name))
        .map((site) => {
            return {
                _id: site._id,
                content: <SiteWithoutSetupTrelloItem site={site} />,
            };
        });

    const initialData: TrelloViewDataType = {
        columns: [
            {
                title: 'Site sans rangement',
                items: siteWithoutSetupItems,
            },
            {
                title: 'En retards',
                items: getSiteSetupsByState({ isLate: true }),
            },
            {
                title: 'Créés',
                items: getSiteSetupsByState({ state: SiteSetupState.Created }),
            },
            {
                title: 'En rangement',
                items: getSiteSetupsByState({ state: SiteSetupState.InProgress }),
            },
            {
                title: 'Finis',
                items: getSiteSetupsByState({ state: SiteSetupState.Finished }),
            },
        ],
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.siteSetups} />
                </HeaderTitle>
                <Filters>
                    <SearchContainer>
                        <TotemInput
                            label="Choisir un rangement"
                            onChange={setSearchInputValue}
                            placeholder="Rechercher par ID, site ou nom"
                            value={searchInputValue}
                        />
                    </SearchContainer>
                </Filters>
                <DateContainer>
                    <TotemDatePicker
                        label="Date des rangements"
                        sublabel="Rangements à cette date ou rangements en retard depuis 2 semaines"
                        selected={setupsDate}
                        onChange={(date) => {
                            setSetupsDate(date as Date);
                        }}
                    />
                </DateContainer>
            </Header>
            <Content>
                <TrelloViewContainer>
                    <TrelloView columnWidth="300px" initialData={initialData} />
                </TrelloViewContainer>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const DateContainer = styled.div`
    min-width: 300px;
    justify-self: end;
`;

const TrelloViewContainer = styled.div`
    overflow-x: scroll;
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const Filters = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 10px;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const SearchContainer = styled.div`
    width: 400px;
`;
